import React from 'react';
const { useState } = React;
import { fmtInTz } from '../coachingTime.js';
import { JourneyHome, JourneyHistory, ResultsPanel, ProgressRing, MilestonePath } from './JourneyViews.jsx';

// The person's space: one card per process and the Diagnostic detail. The same
// components render /espacio and the coach's preview ("Ver lo que ve"), so the
// preview is exactly what the person sees. They only show what the server sent
// (shared/personSpace.ts); they decide nothing about permissions.

const fmtDay = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00Z`).toLocaleDateString('es', { dateStyle: 'medium' }) : '');
const tzOf = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; } };

function nextLine(next, tz) {
  if (!next) return null;
  const when = !next.when ? '' : next.whenKind === 'deadline' ? ` · hasta el ${fmtDay(next.when)}` : ` · ${fmtInTz(next.when, tz)}`;
  return <>{next.title}{when}</>;
}

export function ProcessCard({ card, onOpen, tz = tzOf() }) {
  return (
    <article className={`card space-card ${card.finished ? 'done' : ''}`} aria-label={card.name}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span className={`badge kind-${card.kind}`}>{card.type}</span>
        <span className={`badge ${card.finished ? '' : 'on'}`}>{card.statusLabel}</span>
      </div>
      <h2 style={{ margin: '10px 0 6px' }}>{card.name}</h2>
      {card.next ? <p style={{ margin: '0 0 12px' }}><span className="muted">{card.finished ? 'Disponible: ' : 'Próximo paso: '}</span><b>{nextLine(card.next, tz)}</b></p>
        : <p className="muted" style={{ margin: '0 0 12px' }}>{card.finished ? 'Proceso finalizado.' : 'Sin pasos pendientes por ahora.'}</p>}
      <button className={`btn ${card.finished ? '' : 'primary'}`} onClick={() => onOpen(card)}>Abrir</button>
    </article>
  );
}

/** Active processes as cards; the finished ones in a separate history. */
export function SpaceCards({ view, onOpen }) {
  const tz = view.person?.timeZone || tzOf();
  return (
    <>
      {!view.active.length ? <div className="card muted">No tienes procesos en curso.</div>
        : <div className="space-grid">{view.active.map(c => <ProcessCard key={c.key} card={c} onOpen={onOpen} tz={tz} />)}</div>}
      {view.history.length > 0 && (
        <details className="history" open={!view.active.length}>
          <summary>Historial · {view.history.length} {view.history.length === 1 ? 'proceso finalizado' : 'procesos finalizados'}</summary>
          <div className="space-grid" style={{ marginTop: 10 }}>{view.history.map(c => <ProcessCard key={c.key} card={c} onOpen={onOpen} tz={tz} />)}</div>
        </details>
      )}
    </>
  );
}

const FORM_STATUS = { not_started: 'Sin empezar', draft: 'En curso', submitted: 'Enviado' };
const ONE_ON_ONE = { scheduled: 'Agendada', done: 'Realizada' };

/** One Diagnostic participation: forms, the 1:1 and the results shared with the person. Never answers. */
export function DiagnosticView({ d, tz = tzOf(), onOpenDocument = null, preview = false, journey = null, courses = [], onOpenCourse = null, onStart = null }) {
  // Mi recorrido: the approved results of this participation (only what the server sent)
  const result = journey?.results?.find(r => r.participantId === d.card.id) || null;
  return (
    <>
      <div className="card">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <span className="badge kind-diagnostic">Diagnóstico comercial</span>
          <span className={`badge ${d.card.finished ? '' : 'on'}`}>{d.card.statusLabel}</span>
        </div>
        <h1 style={{ marginTop: 10 }}>{d.company || 'Diagnóstico comercial'}</h1>
        <p className="muted" style={{ margin: 0 }}>Acá ves el estado de tus cuestionarios, tu entrevista y los resultados que se compartieron contigo. Tus respuestas no se muestran en este espacio.</p>
      </div>
      {journey && d.card.progress && (
        <div className="card">
          <h2>Tu avance en este diagnóstico</h2>
          <div className="jr-card-head" style={{ alignItems: 'flex-start' }}>
            <ProgressRing metric={d.card.progress.metric} />
            <div style={{ flex: 1, minWidth: 0 }}><MilestonePath steps={d.card.progress.stages} tz={tz} title="Etapas del diagnóstico" /></div>
          </div>
        </div>
      )}
      {result && <ResultsPanel result={result} journey={journey} courses={courses} onOpenCourse={onOpenCourse} onStart={preview ? null : onStart} />}
      <div className="card">
        <h2>Cuestionarios</h2>
        {!d.forms.length ? <p className="muted">No tienes cuestionarios en este diagnóstico.</p> : d.forms.map(f => (
          <div key={f.key} className="item row" style={{ justifyContent: 'space-between' }}><span>{f.label}</span><span className={`badge ${f.status === 'submitted' ? 'on' : ''}`}>{FORM_STATUS[f.status] || f.status}</span></div>
        ))}
        {d.formLink ? (
          <div className="row" style={{ marginTop: 10 }}>
            {preview ? <span className="badge on">Formulario abierto</span> : <a className="btn primary" href={d.formLink} target="_blank" rel="noopener noreferrer">Responder</a>}
            {d.deadline && <span className="muted">Hasta el {fmtDay(d.deadline)}</span>}
          </div>
        ) : d.forms.some(f => f.status !== 'submitted') && <p className="muted" style={{ marginTop: 8 }}>El plazo para responder terminó.</p>}
      </div>
      <div className="card">
        <h2>Entrevista 1:1</h2>
        {!d.sessions.length ? <p className="muted">Todavía no está agendada.</p> : d.sessions.map((s, i) => (
          <div key={i} className="item row" style={{ justifyContent: 'space-between' }}><span>{s.at ? fmtInTz(s.at, tz) : 'Fecha a confirmar'}</span><span className={`badge ${s.status === 'done' ? 'on' : ''}`}>{ONE_ON_ONE[s.status] || s.status}</span></div>
        ))}
      </div>
      <div className="card">
        <h2>{journey ? 'Informes autorizados' : 'Resultados compartidos'}</h2>
        {!d.documents.length ? <p className="muted">Cuando se compartan contigo, aparecen acá.</p> : d.documents.map(doc => (
          <div key={doc.id} className="item row" style={{ justifyContent: 'space-between' }}>
            <span><b>{doc.title}</b>{doc.publishedAt && <span className="muted"> · {fmtDay(doc.publishedAt)}</span>}</span>
            {preview ? <span className="muted">Se abre en su espacio</span> : <button className="btn small" onClick={() => onOpenDocument(doc)}>Abrir</button>}
          </div>
        ))}
      </div>
    </>
  );
}

// ── The sections of the space: Inicio, Propuestas, Diagnósticos, Acompañamiento, Cursos, Historial ──

const PROPOSAL_STATUS = { accepted: 'Aceptada', declined: 'No aceptada', open: 'Para revisar', closed: 'Cerrada' };

/** The sections this person has, in order. A section without content is not shown. */
export function sectionsOf(view, courses = []) {
  const active = view.active || [];
  const out = [{ key: 'inicio', label: view.journey ? 'Mi recorrido' : 'Inicio' }];
  const has = (section) => (view.updates || []).some(u => u.section === section);
  if (view.proposals?.length || has('propuestas')) out.push({ key: 'propuestas', label: 'Propuestas' });
  if (active.some(c => c.kind === 'diagnostic')) out.push({ key: 'diagnosticos', label: 'Diagnósticos' });
  if (active.some(c => c.kind === 'coaching')) out.push({ key: 'acompanamiento', label: 'Acompañamiento' });
  // Leading a team is a different role: its own section, apart from her own processes (also if she takes part)
  if (active.some(c => c.kind === 'sponsor')) out.push({ key: 'equipos', label: 'Equipos que lideras' });
  if (courses.length || has('cursos')) out.push({ key: 'cursos', label: 'Cursos' });
  if (view.history?.length || (view.journey && courses.some(c => c.status === 'completed'))) out.push({ key: 'historial', label: 'Historial' });
  return out;
}

const money = (n, cur) => { try { return new Intl.NumberFormat('es', { style: 'currency', currency: cur || 'USD', maximumFractionDigits: 2 }).format(n); } catch { return `${cur} ${n}`; } };
const totalsText = (p) => (!p.totals ? '' : [p.totals.once ? money(p.totals.once, p.currency) : '', p.totals.monthly ? `${money(p.totals.monthly, p.currency)} por mes` : ''].filter(Boolean).join(' + '));

/**
 * The proposals addressed to this person. One that is open can be accepted here, exactly as it was sent
 * (onAccept: the server checks it is hers and that it did not change since she opened it).
 */
export function ProposalList({ proposals, onAccept = null, onDecline = null }) {
  const [confirming, setConfirming] = useState(null); // { id, mode: 'accept' | 'decline', read, comment }
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({});
  const [done, setDone] = useState('');
  const accept = async (p) => {
    setBusy(true); setMsg(m => ({ ...m, [p.id]: '' })); setDone('');
    try {
      if (confirming?.mode === 'decline') { await onDecline(p, confirming.comment || ''); setDone(`Listo: respondiste que no aceptás «${p.title}». Quedó registrado con la fecha de hoy.`); }
      else { await onAccept(p); setDone(`Listo: aceptaste «${p.title}». Queda registrada con la fecha de hoy.`); }
      setConfirming(null);
    }
    catch (e) { setMsg(m => ({ ...m, [p.id]: e.message })); if (e.code === 'conditions_changed') setConfirming(null); }
    setBusy(false);
  };
  // The ones to review first; then the accepted and closed ones
  const ordered = [...proposals].sort((a, b) => (a.status === 'open' ? 0 : 1) - (b.status === 'open' ? 0 : 1));
  return (
    <>
    {done && <div className="msg ok" role="status">{done}</div>}
    <div className="space-grid">
      {ordered.map(p => (
        <article key={p.id} className="card space-card" aria-label={p.title}>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span className="badge kind-proposal">Propuesta</span>
            <span className={`badge ${p.status === 'accepted' ? '' : 'on'}`}>{PROPOSAL_STATUS[p.status] || p.status}</span>
          </div>
          <h2 style={{ margin: '10px 0 6px' }}>{p.title}</h2>
          {totalsText(p) && <p style={{ margin: '0 0 6px', fontWeight: 600 }}>{totalsText(p)}</p>}
          {p.status === 'accepted'
            ? <p className="muted" style={{ margin: '0 0 12px' }}>{p.acceptedAt ? `Aceptada el ${fmtDay(p.acceptedAt)}` : 'Aceptada'}{p.acceptedHere ? ' desde tu espacio' : ''}.</p>
            : p.status === 'declined' ? <p className="muted" style={{ margin: '0 0 12px' }}>{p.declinedAt ? `Respondiste el ${fmtDay(p.declinedAt)} que no la aceptás` : 'No aceptada'}{p.declinedWithComment ? '. Tu comentario quedó registrado' : ''}.</p>
            : p.updatedAt && <p className="muted" style={{ margin: '0 0 12px' }}>{p.updatedAt !== p.publishedAt ? `Actualizada el ${fmtDay(p.updatedAt)}` : `Enviada el ${fmtDay(p.publishedAt)}`}</p>}
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <a className="btn primary" href={p.link} target="_blank" rel="noopener noreferrer">Ver cotización</a>
            {onAccept && p.status === 'open' && p.canAccept && confirming?.id !== p.id && <button type="button" className="btn" onClick={() => { setConfirming({ id: p.id, mode: 'accept', read: false }); setMsg(m => ({ ...m, [p.id]: '' })); }}>Aceptar cotización</button>}
            {onDecline && p.status === 'open' && p.canAccept && confirming?.id !== p.id && <button type="button" className="btn" onClick={() => { setConfirming({ id: p.id, mode: 'decline', read: false, comment: '' }); setMsg(m => ({ ...m, [p.id]: '' })); }}>No aceptar</button>}
          </div>
          {p.status === 'open' && p.updating && <p className="muted" style={{ marginTop: 10 }}>Tu propuesta se está actualizando. Podrás aceptarla cuando esté lista.</p>}
          {confirming?.id === p.id && confirming.mode === 'accept' && (
            <div className="item" style={{ marginTop: 10 }} role="group" aria-label="Confirmar aceptación">
              <p style={{ margin: '0 0 8px' }}>Vas a aceptar «{p.title}»{totalsText(p) ? ` por ${totalsText(p)}` : ''}, tal como está en el documento.</p>
              <label className="row" style={{ gap: 6, alignItems: 'flex-start' }}><input type="checkbox" checked={confirming.read} onChange={e => setConfirming({ ...confirming, read: e.target.checked })} /> Leí la cotización y la acepto.</label>
              <div className="row" style={{ gap: 8, marginTop: 8 }}>
                <button type="button" className="btn primary" disabled={busy || !confirming.read} onClick={() => accept(p)}>{busy ? 'Aceptando…' : 'Confirmar aceptación'}</button>
                <button type="button" className="btn" disabled={busy} onClick={() => setConfirming(null)}>Cancelar</button>
              </div>
            </div>
          )}
          {confirming?.id === p.id && confirming.mode === 'decline' && (
            <div className="item" style={{ marginTop: 10 }} role="group" aria-label="Confirmar que no la aceptás">
              <p style={{ margin: '0 0 8px' }}>Vas a responder que no aceptás «{p.title}»{totalsText(p) ? ` (${totalsText(p)})` : ''}.</p>
              <label htmlFor={`decline-${p.id}`} className="muted" style={{ fontSize: 13 }}>Comentario (opcional)</label>
              <textarea id={`decline-${p.id}`} maxLength={500} value={confirming.comment} onChange={e => setConfirming({ ...confirming, comment: e.target.value })} placeholder="Si querés, contanos por qué" />
              <label className="row" style={{ gap: 6, alignItems: 'flex-start', marginTop: 6 }}><input type="checkbox" checked={confirming.read} onChange={e => setConfirming({ ...confirming, read: e.target.checked })} /> Confirmo que no acepto esta cotización.</label>
              <div className="row" style={{ gap: 8, marginTop: 8 }}>
                <button type="button" className="btn primary" disabled={busy || !confirming.read} onClick={() => accept(p)}>{busy ? 'Enviando…' : 'Confirmar: no acepto'}</button>
                <button type="button" className="btn" disabled={busy} onClick={() => setConfirming(null)}>Volver</button>
              </div>
            </div>
          )}
          {msg[p.id] && <div className="msg err" role="alert" style={{ marginTop: 8 }}>{msg[p.id]}</div>}
        </article>
      ))}
    </div>
    </>
  );
}

/** Updates your coach shared with you (only shared ones arrive), optionally of one section. */
export function UpdatesList({ updates = [], section = null, limit = 0, title = 'Avances de tu coach' }) {
  const list = (section ? updates.filter(u => u.section === section) : updates).slice(0, limit || undefined);
  if (!list.length) return null;
  return (
    <div className="card">
      <h2>{title}</h2>
      {list.map(u => (
        <div key={u.id} className="item">
          <div className="muted" style={{ fontSize: 13 }}>{fmtDay(u.at)} · {u.label}</div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{u.text}</div>
        </div>
      ))}
    </div>
  );
}

/** Inicio: what comes next, from every section (the server decides each step; nothing is computed here). */
/** An open proposal to review, on the home of both modes: it goes to «Propuestas», where it is read and accepted. */
function ProposalNotice({ proposals = [], onProposals }) {
  const open = proposals.filter(p => p.status === 'open');
  if (!open.length) return null;
  return (
    <div className="card row" style={{ justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }} aria-label="Propuestas para revisar">
      <span><b>{open.length === 1 ? 'Tienes una propuesta para revisar' : `Tienes ${open.length} propuestas para revisar`}</b><span className="muted"> · {open.map(p => p.title).join(' · ')}</span></span>
      {onProposals && <button type="button" className="btn small" onClick={onProposals}>Ver propuestas</button>}
    </div>
  );
}

export function SpaceHome({ view, courses = [], onOpen, onOpenCourse, onStart = null, preview = false, onProposals = null }) {
  if (view.journey) return <><ProposalNotice proposals={view.proposals} onProposals={onProposals} /><JourneyHome view={view} courses={courses} onOpen={onOpen} onOpenCourse={onOpenCourse} onStart={onStart} preview={preview} updates={<UpdatesList updates={view.updates} limit={5} />} /></>;
  const tz = view.person?.timeZone || tzOf();
  const steps = (view.active || []).filter(c => c.next);
  const openProposals = (view.proposals || []).filter(p => p.status === 'open');
  const learning = courses.filter(c => c.next || c.waiting);
  const nothing = !steps.length && !openProposals.length && !learning.length;
  return (
    <>
      <div className="card">
        <h2>Próximos pasos</h2>
        {nothing && <p className="muted">No tienes pendientes por ahora.</p>}
        {steps.map(c => (
          <div key={c.key} className="item row" style={{ justifyContent: 'space-between' }}>
            <span><span className="muted">{c.name} · </span><b>{nextLine(c.next, tz)}</b></span>
            <button className="btn small" onClick={() => onOpen(c)}>Abrir</button>
          </div>
        ))}
        {learning.map(c => (
          <div key={c.id} className="item row" style={{ justifyContent: 'space-between' }}>
            <span><span className="muted">Curso «{c.courseName}» · </span><b>{c.waiting ? 'Entrega en revisión' : c.next.title}</b></span>
            {onOpenCourse && <button className="btn small" onClick={() => onOpenCourse(c, c.next?.id || null)}>Abrir</button>}
          </div>
        ))}
        {openProposals.map(p => (
          <div key={p.id} className="item row" style={{ justifyContent: 'space-between' }}>
            <span><span className="muted">Propuesta para revisar · </span><b>{p.title}</b></span>
            {onProposals ? <button type="button" className="btn small" onClick={onProposals}>Ver</button> : <a className="btn small" href={p.link} target="_blank" rel="noopener noreferrer">Ver</a>}
          </div>
        ))}
      </div>
      <UpdatesList updates={view.updates} limit={5} />
    </>
  );
}

/** One section's cards. */
export function SectionCards({ section, view, onOpen, courses = [], onOpenCourse = null }) {
  if (section === 'historial' && view.journey) return <JourneyHistory view={view} courses={courses} onOpen={onOpen} onOpenCourse={onOpenCourse} />;
  const tz = view.person?.timeZone || tzOf();
  const pick = section === 'historial' ? view.history
    : (view.active || []).filter(c => (section === 'diagnosticos' ? c.kind === 'diagnostic' : section === 'equipos' ? c.kind === 'sponsor' : c.kind === 'coaching'));
  const updates = section === 'historial' ? [] : (view.updates || []).filter(u => u.section === section);
  if (!pick.length && !updates.length) return <div className="card muted">Nada por acá todavía.</div>;
  return <>
    {pick.length > 0 && <div className="space-grid">{pick.map(c => <ProcessCard key={c.key} card={c} onOpen={onOpen} tz={tz} />)}</div>}
    <UpdatesList updates={updates} title="Avances compartidos" />
  </>;
}

/** A shared deliverable, rendered inside the space (sandboxed: no scripts, no access to the page). */
export function DocumentFrame({ html, title }) {
  return <iframe title={title} srcDoc={html} sandbox="allow-popups" style={{ width: '100%', minHeight: '75vh', border: '1px solid var(--border)', borderRadius: 12, background: '#fff' }} />;
}

export function SpaceTabs({ sections, section, onSection }) {
  if (sections.length < 2) return null;
  return (
    <nav className="tabs space-tabs" aria-label="Secciones de tu espacio">
      {sections.map(x => <button key={x.key} className={x.key === section ? 'on' : ''} aria-current={x.key === section ? 'page' : undefined} onClick={() => onSection(x.key)}>{x.label}</button>)}
    </nav>
  );
}
